/**
 * Componente: CampaignExecutionLogs
 * ---------------------------------------------------------------------------
 * Exibe logs de execução da campanha com paginação SERVER-SIDE, filtros
 * server-side e sem Realtime.
 *
 * Motivação (jul/2026):
 *   A versão anterior fazia SELECT direto em `campaign_logs` (limit 500) e
 *   mantinha um canal Realtime INSERT ativo. Durante disparos de campanhas
 *   grandes isso gerava pico de CPU no Postgres e no cliente. Agora:
 *     - Paginação via RPC `get_campaign_logs` (30 registros por página).
 *     - Filtros (nível/evento/busca) aplicados no banco.
 *     - Zero Realtime: usuário clica em "Atualizar" ou depende do polling
 *       de 15s controlado pela tela CampaignDetails.
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  RefreshCw, Search, ChevronDown, ChevronRight,
  Play, Lock, Phone, CheckCircle, XCircle,
  CheckCircle2, AlertTriangle, Package, Flag,
  RefreshCcw, Send, Clock, FileText
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { useCampaignLogsPaginated, type CampaignLogRow } from '@/hooks/useCampaignLogsPaginated';

// Props do componente
interface CampaignExecutionLogsProps {
  campaignId: string;
}

// Configuração visual por tipo de evento
const eventTypeConfig: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  campaign_started: { icon: <Play className="h-3.5 w-3.5" />, color: 'text-blue-500', label: 'Iniciado' },
  campaign_completed: { icon: <Flag className="h-3.5 w-3.5" />, color: 'text-green-600', label: 'Finalizado' },
  recipient_reserved: { icon: <Lock className="h-3.5 w-3.5" />, color: 'text-purple-500', label: 'Reservado' },
  recipient_skipped: { icon: <AlertTriangle className="h-3.5 w-3.5" />, color: 'text-yellow-500', label: 'Ignorado' },
  whatsapp_check: { icon: <Phone className="h-3.5 w-3.5" />, color: 'text-cyan-500', label: 'Verificação' },
  contact_created: { icon: <CheckCircle className="h-3.5 w-3.5" />, color: 'text-indigo-500', label: 'Contato' },
  conversation_created: { icon: <FileText className="h-3.5 w-3.5" />, color: 'text-indigo-500', label: 'Conversa' },
  item_sent: { icon: <Send className="h-3.5 w-3.5" />, color: 'text-green-500', label: 'Enviado' },
  item_failed: { icon: <XCircle className="h-3.5 w-3.5" />, color: 'text-red-500', label: 'Falha' },
  recipient_completed: { icon: <CheckCircle2 className="h-3.5 w-3.5" />, color: 'text-emerald-500', label: 'Concluído' },
  recipient_failed: { icon: <XCircle className="h-3.5 w-3.5" />, color: 'text-red-600', label: 'Falhou' },
  batch_completed: { icon: <Package className="h-3.5 w-3.5" />, color: 'text-indigo-500', label: 'Lote' },
  stuck_recovered: { icon: <RefreshCcw className="h-3.5 w-3.5" />, color: 'text-orange-500', label: 'Recuperado' },
  delay_applied: { icon: <Clock className="h-3.5 w-3.5" />, color: 'text-muted-foreground', label: 'Delay' },
};

// Configuração visual por nível de log
const levelConfig: Record<string, { className: string; label: string }> = {
  error: { className: 'bg-red-500/10 text-red-600 border-red-500/30', label: 'ERRO' },
  warn:  { className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30', label: 'AVISO' },
  info:  { className: 'bg-blue-500/10 text-blue-600 border-blue-500/30', label: 'INFO' },
  debug: { className: 'bg-muted text-muted-foreground border-muted-foreground/30', label: 'DEBUG' },
};

/**
 * Renderiza uma única linha de log com metadata colapsável.
 */
function LogEntry({ log }: { log: CampaignLogRow }) {
  const [isOpen, setIsOpen] = useState(false);
  const eventConfig = eventTypeConfig[log.event_type] || {
    icon: <FileText className="h-3.5 w-3.5" />,
    color: 'text-muted-foreground',
    label: log.event_type,
  };
  const level = levelConfig[log.log_level] || levelConfig.info;
  const hasMetadata = log.metadata && Object.keys(log.metadata).length > 0;

  return (
    <div className="border-b border-border/50 last:border-0">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="flex items-start gap-3 p-3 hover:bg-muted/30 transition-colors">
          <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 font-mono shrink-0', level.className)}>
            {level.label}
          </Badge>
          <div className={cn('shrink-0 mt-0.5', eventConfig.color)}>
            {eventConfig.icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                {eventConfig.label}
              </Badge>
              <span className="text-[10px] text-muted-foreground">
                {format(new Date(log.created_at), 'HH:mm:ss', { locale: ptBR })}
              </span>
            </div>
            <p className="text-sm text-foreground/90 break-words">
              {log.message}
            </p>
          </div>
          {hasMetadata && (
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0">
                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>
          )}
        </div>
        {hasMetadata && (
          <CollapsibleContent>
            <div className="px-3 pb-3 ml-[72px]">
              <pre className="text-xs bg-muted/50 p-2 rounded-md overflow-x-auto text-muted-foreground">
                {JSON.stringify(log.metadata, null, 2)}
              </pre>
            </div>
          </CollapsibleContent>
        )}
      </Collapsible>
    </div>
  );
}

// Componente principal — logs com paginação server-side
export function CampaignExecutionLogs({ campaignId }: CampaignExecutionLogsProps) {
  // Estado local dos filtros + paginação (controlado pelo usuário)
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [eventFilter, setEventFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);

  // Hook paginado — refetchInterval desativado (polling agora fica na tela pai)
  const {
    items: logs,
    totalCount,
    isLoading,
    isFetching,
    refetch,
  } = useCampaignLogsPaginated({
    campaignId,
    page,
    pageSize,
    level: levelFilter,
    event: eventFilter,
    search: searchTerm,
    enabled: !!campaignId,
  });

  // Handlers para reset de paginação quando filtros mudam
  const handleLevelChange = (v: string) => { setLevelFilter(v); setPage(1); };
  const handleEventChange = (v: string) => { setEventFilter(v); setPage(1); };
  const handleSearchChange = (v: string) => { setSearchTerm(v); setPage(1); };

  const handleRefresh = () => { refetch(); };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="bg-muted/30 border-b pb-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Logs de Execução
            {totalCount > 0 && (
              <Badge variant="secondary" className="ml-2">
                {totalCount} registros
              </Badge>
            )}
          </CardTitle>

          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isFetching}
            className="gap-2"
          >
            <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
            Atualizar
          </Button>
        </div>

        {/* Filtros — todos aplicados no servidor */}
        <div className="flex flex-wrap items-center gap-3 mt-4">
          <Select value={levelFilter} onValueChange={handleLevelChange}>
            <SelectTrigger className="w-[140px] h-9">
              <SelectValue placeholder="Nível" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos níveis</SelectItem>
              <SelectItem value="error">Erros</SelectItem>
              <SelectItem value="warn">Avisos</SelectItem>
              <SelectItem value="info">Info</SelectItem>
              <SelectItem value="debug">Debug</SelectItem>
            </SelectContent>
          </Select>

          <Select value={eventFilter} onValueChange={handleEventChange}>
            <SelectTrigger className="w-[180px] h-9">
              <SelectValue placeholder="Tipo de evento" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos eventos</SelectItem>
              {Object.entries(eventTypeConfig).map(([key, config]) => (
                <SelectItem key={key} value={key}>
                  <span className="flex items-center gap-2">
                    <span className={config.color}>{config.icon}</span>
                    {config.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por telefone, mensagem..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-start gap-3">
                <Skeleton className="h-5 w-12" />
                <Skeleton className="h-4 w-4" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-4 w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <FileText className="h-12 w-12 text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground font-medium">Nenhum log encontrado</p>
            <p className="text-sm text-muted-foreground/70 mt-1">
              Os logs aparecerão aqui quando a campanha for executada
            </p>
          </div>
        ) : (
          <>
            <ScrollArea className="h-[500px]">
              <div className="divide-y divide-border/50">
                {logs.map((log) => (
                  <LogEntry key={log.id} log={log} />
                ))}
              </div>
            </ScrollArea>

            {/* Rodapé de paginação — padrão do sistema */}
            <div className="px-4 pb-4">
              <PaginationFooter
                totalItems={totalCount}
                currentPage={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                pageSizeOptions={[30, 50, 100]}
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
